import { type Dispatch, useContext, useEffect, useRef } from 'react'

import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { getStateFrameSize } from './size'
import { type FrameBuilderStateAction } from './state-reducer'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderState,
} from './types'
import {
  getIsArtworkImageValid,
  getIsArtworkSizeValid,
  getIsGlassValid,
  getIsProfileValid,
  getIsPassepartoutColorValid,
  getIsPassepartoutSizeValid,
  getIsSpacerSizeValid,
  getIsTextureValid,
} from './validation/state-validation'

/**
 * Manages automatic saving of selected state to committed state.
 */
export const useAutomaticCommittedStateUpdate = (
  selectedState: FrameBuilderState,
  dispatchCommittedState: Dispatch<FrameBuilderStateAction>,
) => {
  const { frameBuilderType, frameBuilderConfiguration } = useContext(
    FrameBuilderSettingsContext,
  )

  const isLoadedRef = useRef(false)

  useEffect(() => {
    if (!isLoadedRef.current) {
      return
    }

    const frameSize = getStateFrameSize(selectedState)

    const isArtworkSizeValid = getIsArtworkSizeValid(
      frameBuilderConfiguration,
      selectedState.artwork.width,
      selectedState.artwork.height,
    )
    const isArtworkImageValid = getIsArtworkImageValid(
      selectedState.artwork.image,
    )
    const isProfileValid = getIsProfileValid(frameSize, selectedState.profile)
    const isTextureValid = getIsTextureValid(
      frameBuilderConfiguration,
      selectedState.profile,
      selectedState.texture,
    )
    const isPassepartoutColorValid = getIsPassepartoutColorValid(
      frameBuilderType,
      selectedState.profile,
      selectedState.passepartout.color,
    )
    const isPassepartoutSizeValid = getIsPassepartoutSizeValid(
      selectedState.artwork.width,
      selectedState.artwork.height,
      selectedState.passepartout.color,
      selectedState.passepartout.width,
      selectedState.passepartout.height,
    )
    const isGlassValid = getIsGlassValid(selectedState.glass)
    const isSpacerSizeValid = getIsSpacerSizeValid(
      selectedState.profile,
      selectedState.spacerSize,
    )

    // Artwork size
    if (isArtworkSizeValid) {
      dispatchCommittedState({
        type: 'artworkSize',
        width: selectedState.artwork.width,
        height: selectedState.artwork.height,
      })
    }

    // Artwork image
    if (isArtworkImageValid) {
      dispatchCommittedState({
        type: 'artworkImage',
        url: selectedState.artwork.image?.url,
        name: selectedState.artwork.image?.name,
      })
    }

    dispatchCommittedState({
      type: 'artworkImage',
      color: selectedState.artwork.backgroundColor?.current,
    })

    // Moulding and texture
    if (isProfileValid && isTextureValid) {
      dispatchCommittedState({
        type: 'frameStyle',
        profile: selectedState.profile,
        texture: selectedState.texture,
      })
    }

    // Mount color and size
    if (isPassepartoutColorValid && isPassepartoutSizeValid) {
      dispatchCommittedState({
        type: 'passepartout',
        color: selectedState.passepartout.color,
        width: selectedState.passepartout.width,
        height: selectedState.passepartout.height,
      })
    }

    // Glass
    if (isGlassValid) {
      dispatchCommittedState({
        type: 'glass',
        glass: selectedState.glass,
      })
    }

    // Spacer size and color
    if (isSpacerSizeValid) {
      dispatchCommittedState({
        type: 'spacer',
        size: selectedState.spacerSize,
        color: selectedState.spacerColor,
      })
    }
  }, [
    dispatchCommittedState,
    frameBuilderConfiguration,
    frameBuilderType,
    selectedState,
  ])

  useEffect(() => {
    isLoadedRef.current = true
  }, [])
}

/**
 * Manages automatic updates to selected state.
 */
export const useAutomaticSelectedStateUpdate = (
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  selectedState: FrameBuilderState,
  dispatchSelectedState: Dispatch<FrameBuilderStateAction>,
) => {
  // Floating frame
  useEffect(() => {
    if (!selectedState.profile?.floatingFrame) {
      return
    }

    // Deselect mount
    if (typeof selectedState.passepartout.color !== 'undefined') {
      dispatchSelectedState({
        type: 'passepartout',
        color: undefined,
      })
    }

    // Deselect glass
    const glassNone = frameBuilderConfiguration.glass.find(
      (glass) => glass.thickness === 0,
    )

    if (
      typeof selectedState.glass !== 'undefined' &&
      selectedState.glass.thickness > 0 &&
      typeof glassNone !== 'undefined'
    ) {
      dispatchSelectedState({
        type: 'glass',
        glass: glassNone,
      })
    }

    // Deselect spacer
    const spacerSizeNone = frameBuilderConfiguration.spacerSizes.find(
      (spacerSize) => spacerSize.thickness === 0,
    )

    if (
      typeof selectedState.spacerSize !== 'undefined' &&
      selectedState.spacerSize.thickness > 0 &&
      typeof spacerSizeNone !== 'undefined'
    ) {
      dispatchSelectedState({
        type: 'spacer',
        size: spacerSizeNone,
      })
    }
  }, [dispatchSelectedState, frameBuilderConfiguration, selectedState])
}
